import * as numbers from "common/numbers";
import * as strings from "common/strings";
import * as pricing from "domain/pricing";

export const resolveShares = (shares, { layer }) => {
  if (shares == null || shares.length === 0) {
    return [];
  }

  return shares.map((share) => {
    const sharePct = numbers.parseFloatOrNull(
      share.shareOfLimit != null
        ? !!layer.limit
          ? (100 * share.shareOfLimit) / layer.limit
          : null
        : share.share ?? null
    );

    const apportion = (value) =>
      sharePct != null && value != null ? 0.01 * sharePct * value : null;

    const shareOfLimit = numbers.parseFloatOrNull(
      share.shareOfLimit ?? apportion(layer.limit)
    );
    const shareOfGrossPremium = numbers.parseFloatOrNull(
      share.shareOfGrossPremium ??
        pricing.convertFromRPM({
          rpm: share.grossRPM,
          layer: { limit: shareOfLimit },
        }) ??
        apportion(layer.grossPremium) ??
        apportion(
          pricing.convertFromRPM({
            rpm: layer.grossRPM,
            layer: { limit: shareOfLimit },
          })
        )
    );
    const brokerage = numbers.parseFloatOrNull(share.brokerage ?? null);
    const shareOfNetPremium = numbers.parseFloatOrNull(
      shareOfGrossPremium != null && brokerage != null
        ? (1 - 0.01 * brokerage) * shareOfGrossPremium
        : null
    );

    return {
      ...share,
      carrier: share.carrier || null,
      share: sharePct,
      shareOfLimit,
      shareOfGrossPremium,
      grossRPM: pricing.convertToRPM({
        price: shareOfGrossPremium,
        layer: { limit: shareOfLimit },
      }),
      brokerage,
      shareOfNetPremium,
      comments: share.comments || null,
      reference: share.reference || null,
    };
  });
};

export const isCoverageSplit = (shares) =>
  (shares?.length ?? 0) > 1 &&
  shares.every(({ share }) => numbers.approxEqual(share, 100)) &&
  new Set(shares.map(({ coverage }) => coverage)).size === shares.length;

const checkErrorCalculatingNetValues = (shares, { layer }) => {
  if (!shares?.length) return "No shares";

  const limit = shares
    .map((s) => s.shareOfLimit ?? 0)
    .reduce((a, b) => a + b, 0);
  if (
    !numbers.approxEqual(limit, layer.limit) &&
    !(
      shares.every(({ share }) => numbers.approxEqual(share, 100)) &&
      new Set(shares.map(({ coverage }) => coverage)).size === shares.length
    )
  )
    return "Share limits don't add up to the layer limit";

  const grossPremium = shares
    .map((s) => s.shareOfGrossPremium ?? 0)
    .reduce((a, b) => a + b, 0);
  if (!numbers.approxEqual(grossPremium, layer.grossPremium))
    return "Share premiums don't add up to the layer premium";

  if (shares.some((s) => s.brokerage == null))
    return "Not all shares have a brokerage specified";

  return null;
};

const resolveSir = (layer, { selfInsuredRetentions }) => {
  const includedSir = layer?.includeSir
    ? (selfInsuredRetentions || []).find(
        (sir) => sir.id === layer.includeSir
      ) ?? null
    : null;
  const sirValue = includedSir?.value ?? null;
  const effectiveAttachment =
    layer?.attachment != null ? layer.attachment + (sirValue ?? 0) : null;
  return { sir: sirValue, effectiveAttachment };
};

export const resolveLayer = (
  layer,
  { program, selfInsuredRetentions = [] }
) => {
  if (!layer) {
    return null;
  }

  const errors = {};
  const shares = resolveShares(layer.shares, { layer });

  const grossPremium =
    layer.grossPremium ??
    pricing.convertFromRPM({ rpm: layer.grossRPM, layer }) ??
    (shares.some(({ shareOfGrossPremium }) => shareOfGrossPremium != null)
      ? shares
          .map(({ shareOfGrossPremium }) => shareOfGrossPremium)
          .reduce((a, b) => a + b, 0)
      : null);

  const errorCalculatingNetValues = checkErrorCalculatingNetValues(shares, {
    layer: {
      ...layer,
      grossPremium,
    },
  });
  if (errorCalculatingNetValues) {
    errors.netPremium = errorCalculatingNetValues;
  }
  const hasNet = !errorCalculatingNetValues;

  const netPremium = hasNet
    ? shares.map((s) => s.shareOfNetPremium ?? 0).reduce((a, b) => a + b, 0)
    : null;

  const { sir, effectiveAttachment } = resolveSir(layer, {
    selfInsuredRetentions,
  });

  return {
    ...layer,
    sir,
    effectiveAttachment,
    grossPremium,
    grossRPM: pricing.convertToRPM({ price: grossPremium, layer }),
    netPremium,
    netRPM: pricing.convertToRPM({ price: netPremium, layer }),
    brokerage:
      netPremium != null && grossPremium
        ? (100 * (grossPremium - netPremium)) / grossPremium
        : null,
    shares,
    errors,
    inception: layer.inception ?? program?.inception,
    expiration: layer.expiration ?? program?.expiration,
    reference: (layer.shares ?? []).some((_) => strings.hasValue(_.reference))
      ? (layer.shares ?? []).map((_) => _.reference ?? "").join(" / ")
      : null,
  };
};

export const resolveLayers = (
  layers,
  { program, selfInsuredRetentions = [] }
) => {
  if (layers == null) {
    return null;
  }
  return layers
    .map((layer) => resolveLayer(layer, { program, selfInsuredRetentions }))
    .reduce((layersBelow, layer) => {
      const layerBelow = layersBelow[layersBelow.length - 1] ?? null;

      const inputLayer =
        layer.grossRPMRelativeToLayerBelow != null
          ? {
              ...layer,
              grossPremium: null,
              grossRPM:
                Number.isFinite(layer.grossRPMRelativeToLayerBelow) &&
                Number.isFinite(layerBelow.grossRPM)
                  ? layer.grossRPMRelativeToLayerBelow * layerBelow.grossRPM
                  : 0,
            }
          : layer;

      const resolvedLayer = resolveLayer(inputLayer, {
        program,
        selfInsuredRetentions,
      });

      const calculateRelativeToLayerBelow = (key) =>
        resolvedLayer[key] != null && layerBelow?.[key]
          ? resolvedLayer[key] / layerBelow[key]
          : null;

      return [
        ...layersBelow,
        {
          ...resolvedLayer,
          grossRPMRelativeToLayerBelow: calculateRelativeToLayerBelow(
            "grossRPM"
          ),
          netRPMRelativeToLayerBelow: calculateRelativeToLayerBelow("netRPM"),
        },
      ];
    }, []);
};
